import { Archive, Award, Baby, Box, Circle, Crown, Droplets, Feather, Flame, Flower2, Gem, Globe, Heart, Layers, Leaf, Moon, Package, Palette, Recycle, RefreshCw, Ruler, ShieldCheck, Smile, Snowflake, Sparkles, Star, Sun, Tag, Thermometer, TreePine, UserCheck, Users, Wind, Zap } from "lucide-react";
import React from "react";

/* ──────────────────────────────────────────────
   TYPES
   Single unified shape for both a leaf attribute
   value ("Red", "128 GB") and a group that holds
   a list of values ("Color" -> [Red, Blue, ...]).
   A group is just an AttributeItem with `values`.
────────────────────────────────────────────── */

export interface AttributeItem {
  id: number;
  name: string;
  icon?: React.ReactNode;
  hex?: string;
  hexCodes?: string[];
  gradient?: string;
  numericValue?: number;
  unit?: string;
  parentId?: number | null;
    description?: string; 
  appliesTo?: number[];

  values?: AttributeItem[];
}


/* ═══════════════════════════════════════════════════════════
   📦 PACKAGING TYPE
═══════════════════════════════════════════════════════════ */
export const PackagingTypes: AttributeItem[] = [
  { id: 90301, name: "Loose",   icon: <Package size={14} /> },
  { id: 90302, name: "Bag",     icon: <Package size={14} /> },
  { id: 90303, name: "Box",     icon: <Box size={14} /> },
  { id: 90304, name: "Jar",     icon: <Circle size={14} /> },
  { id: 90305, name: "Bottle",  icon: <Droplets size={14} /> },
  { id: 90306, name: "Can",     icon: <Circle size={14} /> },
  { id: 90307, name: "Pouch",   icon: <Recycle size={14} /> },
  { id: 90308, name: "Tray",    icon: <Layers size={14} /> },
  { id: 90309, name: "Carton",  icon: <Archive size={14} /> },
  { id: 90310, name: "Wrapped", icon: <Tag size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   ⚖️ WEIGHT / VOLUME UNIT
═══════════════════════════════════════════════════════════ */
export const WeightUnits: AttributeItem[] = [
  { id: 90401, name: "Gram",      unit: "g" },
  { id: 90402, name: "Kilogram",  unit: "kg" },
  { id: 90403, name: "Milliliter",unit: "ml" },
  { id: 90404, name: "Liter",     unit: "l" },
];

/* ═══════════════════════════════════════════════════════════
   📏 LENGTH / DIMENSION UNIT
═══════════════════════════════════════════════════════════ */
export const LengthUnits: AttributeItem[] = [
  { id: 90501, name: "Centimeter", unit: "cm" },
  { id: 90502, name: "Inch",       unit: "inch" },
];

/* ═══════════════════════════════════════════════════════════
   ❄️ STORAGE REQUIREMENT
═══════════════════════════════════════════════════════════ */
export const StorageRequirements: AttributeItem[] = [
  { id: 90601, name: "Ambient / Dry", icon: <Sun size={14} />,         description: "Pantry shelf, room temperature" },
  { id: 90602, name: "Refrigerated",  icon: <Thermometer size={14} />, description: "2–5°C cold chain" },
  { id: 90603, name: "Frozen",        icon: <Snowflake size={14} />,   description: "−18°C or below" },
];

export const Units: AttributeItem[] = [
  { id: 1, name: "Gram" },
  { id: 2, name: "Kilogram" },
  { id: 3, name: "Milliliter" },
  { id: 4, name: "Liter"},
  { id: 5, name: "Piece" },
];

/* ═══════════════════════════════════════════════════════════
   1. AGES
═══════════════════════════════════════════════════════════ */
export const AgeRanges: AttributeItem[] = [
  { id: 1001, name: "0–1 month",   icon: <Baby size={16} /> },
  { id: 1002, name: "1–3 months",  icon: <Baby size={16} /> },
  { id: 1003, name: "3–6 months",  icon: <Baby size={16} /> },
  { id: 1004, name: "6–9 months",  icon: <Baby size={16} /> },
  { id: 1005, name: "9–12 months", icon: <Baby size={16} /> },
  { id: 1006, name: "12–18 months",icon: <Baby size={16} /> },
  { id: 1007, name: "18–24 months",icon: <Baby size={16} /> },
  { id: 1008, name: "2–3 years",   icon: <Users size={16} /> },
  { id: 1009, name: "3–4 years",   icon: <Users size={16} /> },
  { id: 1010, name: "4–5 years",   icon: <Users size={16} /> },
  { id: 1011, name: "5–6 years",   icon: <Users size={16} /> },
  { id: 1012, name: "6–7 years",   icon: <Users size={16} /> },
  { id: 1013, name: "7–8 years",   icon: <Users size={16} /> },
  { id: 1014, name: "8–9 years",   icon: <Users size={16} /> },
  { id: 1015, name: "9–10 years",  icon: <Users size={16} /> },
  { id: 1016, name: "10–11 years", icon: <Users size={16} /> },
  { id: 1017, name: "11–12 years", icon: <Users size={16} /> },
  { id: 1018, name: "12–13 years", icon: <UserCheck size={16} /> },
  { id: 1019, name: "13–14 years", icon: <UserCheck size={16} /> },
  { id: 1020, name: "14–15 years", icon: <UserCheck size={16} /> },
  { id: 1021, name: "15–16 years", icon: <UserCheck size={16} /> },
];

/* ═══════════════════════════════════════════════════════════
   5. SHOE SIZES (EU)
═══════════════════════════════════════════════════════════ */
export const ShoeSizes: AttributeItem[] = [
  { id: 5001, name: "EU 39",   icon: <Ruler size={14} /> },
  { id: 5002, name: "EU 39.5", icon: <Ruler size={14} /> },
  { id: 5003, name: "EU 40",   icon: <Ruler size={14} /> },
  { id: 5004, name: "EU 40.5", icon: <Ruler size={14} /> },
  { id: 5005, name: "EU 41",   icon: <Ruler size={14} /> },
  { id: 5006, name: "EU 41.5", icon: <Ruler size={14} /> },
  { id: 5007, name: "EU 42",   icon: <Ruler size={14} /> },
  { id: 5008, name: "EU 42.5", icon: <Ruler size={14} /> },
  { id: 5009, name: "EU 43",   icon: <Ruler size={14} /> },
  { id: 5010, name: "EU 43.5", icon: <Ruler size={14} /> },
  { id: 5011, name: "EU 44",   icon: <Ruler size={14} /> },
  { id: 5012, name: "EU 44.5", icon: <Ruler size={14} /> },
  { id: 5013, name: "EU 45",   icon: <Ruler size={14} /> },
  { id: 5014, name: "EU 45.5", icon: <Ruler size={14} /> },
  { id: 5015, name: "EU 46",   icon: <Ruler size={14} /> },
  { id: 5016, name: "EU 47",   icon: <Ruler size={14} /> },
];

export const Sizes: AttributeItem[] = [
  { id: 2001, name: "XXS",       icon: <Ruler size={14} /> },
  { id: 2002, name: "XS",        icon: <Ruler size={14} /> },
  { id: 2003, name: "S",         icon: <Ruler size={14} /> },
  { id: 2004, name: "M",         icon: <Ruler size={14} /> },
  { id: 2005, name: "L",         icon: <Ruler size={14} /> },
  { id: 2006, name: "XL",        icon: <Ruler size={14} /> },
  { id: 2007, name: "XXL",       icon: <Ruler size={14} /> },
  { id: 2008, name: "3XL",       icon: <Ruler size={14} /> },
  { id: 2009, name: "4XL",       icon: <Ruler size={14} /> },
  { id: 2010, name: "One Size",  icon: <Ruler size={14} /> },
  // Italian / EU numeric
  { id: 2011, name: "IT 36",     icon: <Ruler size={14} /> },
  { id: 2012, name: "IT 38",     icon: <Ruler size={14} /> },
  { id: 2013, name: "IT 40",     icon: <Ruler size={14} /> },
  { id: 2014, name: "IT 42",     icon: <Ruler size={14} /> },
  { id: 2015, name: "IT 44",     icon: <Ruler size={14} /> },
  { id: 2016, name: "IT 46",     icon: <Ruler size={14} /> },
  { id: 2017, name: "IT 48",     icon: <Ruler size={14} /> },
  { id: 2018, name: "IT 50",     icon: <Ruler size={14} /> },
  { id: 2019, name: "IT 52",     icon: <Ruler size={14} /> },
  { id: 2020, name: "IT 54",     icon: <Ruler size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   10. MATERIAL
═══════════════════════════════════════════════════════════ */
export const Materials: AttributeItem[] = [
  { id: 10001, name: "Cotton",       icon: <Leaf size={14} /> },
  { id: 10002, name: "Linen",        icon: <Leaf size={14} /> },
  { id: 10003, name: "Silk",         icon: <Sparkles size={14} /> },
  { id: 10004, name: "Satin",        icon: <Sparkles size={14} /> },
  { id: 10005, name: "Chiffon",      icon: <Wind size={14} /> },
  { id: 10006, name: "Velvet",       icon: <Gem size={14} /> },
  { id: 10007, name: "Cashmere",     icon: <Flame size={14} /> },
  { id: 10008, name: "Wool",         icon: <Flame size={14} /> },
  { id: 10009, name: "Merino Wool",  icon: <Flame size={14} /> },
  { id: 10010, name: "Tweed",        icon: <Leaf size={14} /> },
  { id: 10011, name: "Denim",        icon: <Tag size={14} /> },
  { id: 10012, name: "Leather",      icon: <ShieldCheck size={14} /> },
  { id: 10013, name: "Suede",        icon: <ShieldCheck size={14} /> },
  { id: 10014, name: "Patent Leather",icon: <ShieldCheck size={14} /> },
  { id: 10015, name: "Faux Leather", icon: <Recycle size={14} /> },
  { id: 10016, name: "Nylon",        icon: <Zap size={14} /> },
  { id: 10017, name: "Polyester",    icon: <Zap size={14} /> },
  { id: 10018, name: "Spandex",      icon: <Zap size={14} /> },
  { id: 10019, name: "Jersey",       icon: <Tag size={14} /> },
  { id: 10020, name: "Knit",         icon: <Leaf size={14} /> },
  { id: 10021, name: "Lace",         icon: <Sparkles size={14} /> },
  { id: 10022, name: "Sequin",       icon: <Sparkles size={14} /> },
  { id: 10023, name: "Twill",        icon: <Leaf size={14} /> },
  { id: 10024, name: "Canvas",       icon: <Tag size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   11. FIT / SILHOUETTE
═══════════════════════════════════════════════════════════ */
export const Fits: AttributeItem[] = [
  { id: 11001, name: "Slim Fit",       icon: <Ruler size={14} /> },
  { id: 11002, name: "Regular Fit",    icon: <Ruler size={14} /> },
  { id: 11003, name: "Relaxed Fit",    icon: <Ruler size={14} /> },
  { id: 11004, name: "Oversized",      icon: <Ruler size={14} /> },
  { id: 11005, name: "Cropped",        icon: <Ruler size={14} /> },
  { id: 11006, name: "Maxi",           icon: <Ruler size={14} /> },
  { id: 11007, name: "Midi",           icon: <Ruler size={14} /> },
  { id: 11008, name: "Mini",           icon: <Ruler size={14} /> },
  { id: 11009, name: "Bodycon",        icon: <Ruler size={14} /> },
  { id: 11010, name: "A-Line",         icon: <Ruler size={14} /> },
  { id: 11011, name: "Flared",         icon: <Ruler size={14} /> },
  { id: 11012, name: "Straight Leg",   icon: <Ruler size={14} /> },
  { id: 11013, name: "Wide Leg",       icon: <Ruler size={14} /> },
  { id: 11014, name: "Skinny",         icon: <Ruler size={14} /> },
  { id: 11015, name: "Tapered",        icon: <Ruler size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   12. SUSTAINABILITY
═══════════════════════════════════════════════════════════ */
export const Sustainability: AttributeItem[] = [
  { id: 12001, name: "Organic Materials",    icon: <Leaf size={14} /> },
  { id: 12002, name: "Recycled Materials",   icon: <Recycle size={14} /> },
  { id: 12003, name: "Sustainable Brand",    icon: <Globe size={14} /> },
  { id: 12004, name: "Vegan",               icon: <Leaf size={14} /> },
  { id: 12005, name: "Cruelty Free",        icon: <ShieldCheck size={14} /> },
  { id: 12006, name: "Fair Trade",          icon: <Award size={14} /> },
  { id: 12007, name: "Carbon Neutral",      icon: <TreePine size={14} /> },
  { id: 12008, name: "Made to Order",       icon: <Sparkles size={14} /> },
  { id: 12009, name: "Upcycled",            icon: <Recycle size={14} /> },
  { id: 12010, name: "Natural Dyes",        icon: <Flower2 size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   7. BRANDS (Fashion)
   Top luxury & contemporary brands as seen on Farfetch
═══════════════════════════════════════════════════════════ */
export const Brands: AttributeItem[] = [
  // Ultra Luxury
  { id: 7001, name: "Hermès",              icon: <Crown size={14} /> },
  { id: 7002, name: "Chanel",             icon: <Crown size={14} /> },
  { id: 7003, name: "Louis Vuitton",      icon: <Crown size={14} /> },
  { id: 7004, name: "Dior",              icon: <Crown size={14} /> },
  { id: 7005, name: "Gucci",             icon: <Crown size={14} /> },
  { id: 7006, name: "Prada",             icon: <Crown size={14} /> },
  { id: 7007, name: "Bottega Veneta",    icon: <Crown size={14} /> },
  { id: 7008, name: "Valentino",         icon: <Crown size={14} /> },
  { id: 7009, name: "Versace",           icon: <Crown size={14} /> },
  { id: 7010, name: "Givenchy",          icon: <Crown size={14} /> },
  { id: 7011, name: "Balenciaga",        icon: <Crown size={14} /> },
  { id: 7012, name: "Saint Laurent",     icon: <Crown size={14} /> },
  { id: 7013, name: "Celine",            icon: <Crown size={14} /> },
  { id: 7014, name: "Loewe",             icon: <Crown size={14} /> },
  { id: 7015, name: "Fendi",             icon: <Crown size={14} /> },
  { id: 7016, name: "Miu Miu",           icon: <Crown size={14} /> },
  { id: 7017, name: "Burberry",          icon: <Crown size={14} /> },
  { id: 7018, name: "Alexander McQueen", icon: <Crown size={14} /> },
  { id: 7019, name: "Dolce & Gabbana",   icon: <Crown size={14} /> },
  { id: 7020, name: "Balmain",           icon: <Crown size={14} /> },
  { id: 7021, name: "Off-White",         icon: <Crown size={14} /> },
  { id: 7022, name: "Tom Ford",          icon: <Crown size={14} /> },
  { id: 7023, name: "Brunello Cucinelli",icon: <Crown size={14} /> },
  { id: 7024, name: "Loro Piana",        icon: <Crown size={14} /> },
  { id: 7025, name: "Zegna",             icon: <Crown size={14} /> },
  // Designer
  { id: 7026, name: "Jacquemus",         icon: <Star size={14} /> },
  { id: 7027, name: "The Row",           icon: <Star size={14} /> },
  { id: 7028, name: "Totême",            icon: <Star size={14} /> },
  { id: 7029, name: "Marni",             icon: <Star size={14} /> },
  { id: 7030, name: "Acne Studios",      icon: <Star size={14} /> },
  { id: 7031, name: "Ami Paris",         icon: <Star size={14} /> },
  { id: 7032, name: "Isabel Marant",     icon: <Star size={14} /> },
  { id: 7033, name: "Zimmermann",        icon: <Star size={14} /> },
  { id: 7034, name: "Self-Portrait",     icon: <Star size={14} /> },
  { id: 7035, name: "Stella McCartney",  icon: <Star size={14} /> },
  { id: 7036, name: "Vivienne Westwood", icon: <Star size={14} /> },
  { id: 7037, name: "Moschino",          icon: <Star size={14} /> },
  { id: 7038, name: "Lanvin",            icon: <Star size={14} /> },
  { id: 7039, name: "Rokh",              icon: <Star size={14} /> },
  { id: 7040, name: "Rick Owens",        icon: <Star size={14} /> },
  // Sportswear Luxury
  { id: 7041, name: "Stone Island",      icon: <Zap size={14} /> },
  { id: 7042, name: "Moncler",           icon: <Zap size={14} /> },
  { id: 7043, name: "Canada Goose",      icon: <Zap size={14} /> },
  { id: 7044, name: "Adidas",            icon: <Zap size={14} /> },
  { id: 7045, name: "Nike",              icon: <Zap size={14} /> },
  { id: 7046, name: "New Balance",       icon: <Zap size={14} /> },
  { id: 7047, name: "Salomon",           icon: <Zap size={14} /> },
  { id: 7048, name: "On Running",        icon: <Zap size={14} /> },
  // Shoe specialists
  { id: 7049, name: "Manolo Blahnik",    icon: <Gem size={14} /> },
  { id: 7050, name: "Christian Louboutin", icon: <Gem size={14} /> },
  { id: 7051, name: "Jimmy Choo",        icon: <Gem size={14} /> },
  { id: 7052, name: "Stuart Weitzman",   icon: <Gem size={14} /> },
  { id: 7053, name: "Common Projects",   icon: <Gem size={14} /> },
  { id: 7054, name: "Golden Goose",      icon: <Gem size={14} /> },
  { id: 7055, name: "Aquazzura",         icon: <Gem size={14} /> },
  // Kids
  { id: 7056, name: "Bonpoint",          icon: <Baby size={14} /> },
  { id: 7057, name: "Moncler Enfant",    icon: <Baby size={14} /> },
  { id: 7058, name: "Burberry Kids",     icon: <Baby size={14} /> },
  { id: 7059, name: "Givenchy Kids",     icon: <Baby size={14} /> },
  { id: 7060, name: "Polo Ralph Lauren Kids", icon: <Baby size={14} /> },
  { id: 7061, name: "PUCCI Junior",      icon: <Baby size={14} /> },
  { id: 7062, name: "Kenzo Kids",        icon: <Baby size={14} /> },
  { id: 7063, name: "Marni Kids",        icon: <Baby size={14} /> },
  { id: 7064, name: "Diesel Kids",       icon: <Baby size={14} /> },
  { id: 7065, name: "Stella McCartney Kids", icon: <Baby size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   15. HEEL HEIGHT (shoes)
═══════════════════════════════════════════════════════════ */
export const HeelHeights: AttributeItem[] = [
  { id: 15001, name: "Flat (0 cm)",        icon: <Ruler size={14} /> },
  { id: 15002, name: "Low (1–3 cm)",       icon: <Ruler size={14} /> },
  { id: 15003, name: "Mid (4–6 cm)",       icon: <Ruler size={14} /> },
  { id: 15004, name: "High (7–9 cm)",      icon: <Ruler size={14} /> },
  { id: 15005, name: "Very High (10+ cm)", icon: <Ruler size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   16. PATTERN
═══════════════════════════════════════════════════════════ */
export const Patterns: AttributeItem[] = [
  { id: 16001, name: "Solid",          icon: <Palette size={14} /> },
  { id: 16002, name: "Stripe",         icon: <Palette size={14} /> },
  { id: 16003, name: "Check",          icon: <Palette size={14} /> },
  { id: 16004, name: "Plaid / Tartan", icon: <Palette size={14} /> },
  { id: 16005, name: "Houndstooth",    icon: <Palette size={14} /> },
  { id: 16006, name: "Floral",         icon: <Flower2 size={14} /> },
  { id: 16007, name: "Leopard",        icon: <Palette size={14} /> },
  { id: 16008, name: "Zebra",          icon: <Palette size={14} /> },
  { id: 16009, name: "Snake",          icon: <Palette size={14} /> },
  { id: 16010, name: "Abstract",       icon: <Palette size={14} /> },
  { id: 16011, name: "Geometric",      icon: <Palette size={14} /> },
  { id: 16012, name: "Logo",           icon: <Crown size={14} /> },
  { id: 16013, name: "Tie-Dye",        icon: <Palette size={14} /> },
  { id: 16014, name: "Paisley",        icon: <Palette size={14} /> },
  { id: 16015, name: "Polka Dot",      icon: <Palette size={14} /> },
  { id: 16016, name: "Camouflage",     icon: <Palette size={14} /> },
];

//=============================================================================
//    BEAUTY ATTRIBUTES
//=============================================================================

/* ═══════════════════════════════════════════════════════════
   1. SKIN TYPE
═══════════════════════════════════════════════════════════ */
export const SkinTypes: AttributeItem[] = [
  { id: 30101, name: "Normal",          icon: <Smile size={14} /> },
  { id: 30102, name: "Dry",             icon: <Leaf size={14} /> },
  { id: 30103, name: "Oily",            icon: <Droplets size={14} /> },
  { id: 30104, name: "Combination",     icon: <RefreshCw size={14} /> },
  { id: 30105, name: "Sensitive",       icon: <Heart size={14} /> },
  { id: 30106, name: "Acne-Prone",      icon: <ShieldCheck size={14} /> },
  { id: 30107, name: "Mature",          icon: <Star size={14} /> },
  { id: 30108, name: "Dehydrated",      icon: <Droplets size={14} /> },
  { id: 30109, name: "Redness-Prone",   icon: <Heart size={14} /> },
  { id: 30110, name: "Eczema-Prone",    icon: <ShieldCheck size={14} /> },
  { id: 30111, name: "All Skin Types",  icon: <Globe size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   2. SKIN CONCERN
═══════════════════════════════════════════════════════════ */
export const SkinConcerns: AttributeItem[] = [
  { id: 30201, name: "Anti-Ageing",        icon: <RefreshCw size={14} /> },
  { id: 30202, name: "Brightening",        icon: <Sparkles size={14} /> },
  { id: 30203, name: "Hydration",          icon: <Droplets size={14} /> },
  { id: 30204, name: "Acne & Blemishes",   icon: <ShieldCheck size={14} /> },
  { id: 30205, name: "Dark Spots",         icon: <Sun size={14} /> },
  { id: 30206, name: "Pores",              icon: <Circle size={14} /> },
  { id: 30207, name: "Fine Lines & Wrinkles", icon: <Zap size={14} /> },
  { id: 30208, name: "Dark Circles",       icon: <Moon size={14} /> },
  { id: 30209, name: "Firmness & Elasticity", icon: <Zap size={14} /> },
  { id: 30210, name: "Uneven Skin Tone",   icon: <Palette size={14} /> },
  { id: 30211, name: "Sensitivity & Redness", icon: <Heart size={14} /> },
  { id: 30212, name: "Dryness & Flaking",  icon: <Leaf size={14} /> },
  { id: 30213, name: "Oiliness",           icon: <Droplets size={14} /> },
  { id: 30214, name: "Stretch Marks",      icon: <ShieldCheck size={14} /> },
  { id: 30215, name: "Sun Damage",         icon: <Sun size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   3. SKIN TONE (makeup shade finder)
═══════════════════════════════════════════════════════════ */
export const SkinTones: AttributeItem[] = [
  { id: 30301, name: "Fair",            hex: "#F8D5B7", icon: <Palette size={14} /> },
  { id: 30302, name: "Light",           hex: "#F0C5A0", icon: <Palette size={14} /> },
  { id: 30303, name: "Light Medium",    hex: "#E0A882", icon: <Palette size={14} /> },
  { id: 30304, name: "Medium",          hex: "#C88A66", icon: <Palette size={14} /> },
  { id: 30305, name: "Medium Tan",      hex: "#B07244", icon: <Palette size={14} /> },
  { id: 30306, name: "Tan",             hex: "#9A5C30", icon: <Palette size={14} /> },
  { id: 30307, name: "Deep Tan",        hex: "#7D4520", icon: <Palette size={14} /> },
  { id: 30308, name: "Deep",            hex: "#5C2E10", icon: <Palette size={14} /> },
  { id: 30309, name: "Rich",            hex: "#3B1A08", icon: <Palette size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   4. UNDERTONE
═══════════════════════════════════════════════════════════ */
export const Undertones: AttributeItem[] = [
  { id: 30401, name: "Cool",    icon: <Moon size={14} /> },
  { id: 30402, name: "Warm",    icon: <Sun size={14} /> },
  { id: 30403, name: "Neutral", icon: <Circle size={14} /> },
  { id: 30404, name: "Olive",   icon: <Leaf size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   5. COVERAGE (makeup)
═══════════════════════════════════════════════════════════ */
export const Coverage: AttributeItem[] = [
  { id: 30501, name: "Sheer",    icon: <Layers size={14} /> },
  { id: 30502, name: "Light",    icon: <Layers size={14} /> },
  { id: 30503, name: "Medium",   icon: <Layers size={14} /> },
  { id: 30504, name: "Full",     icon: <Layers size={14} /> },
  { id: 30505, name: "Buildable",icon: <Layers size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   6. FINISH (makeup)
═══════════════════════════════════════════════════════════ */
export const Finish: AttributeItem[] = [
  { id: 30601, name: "Matte",          icon: <Circle size={14} /> },
  { id: 30602, name: "Dewy",           icon: <Droplets size={14} /> },
  { id: 30603, name: "Satin",          icon: <Sparkles size={14} /> },
  { id: 30604, name: "Luminous",       icon: <Sparkles size={14} /> },
  { id: 30605, name: "Natural",        icon: <Leaf size={14} /> },
  { id: 30606, name: "Glitter",        icon: <Star size={14} /> },
  { id: 30607, name: "Metallic",       icon: <Star size={14} /> },
  { id: 30608, name: "Sheer Glow",     icon: <Sparkles size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   7. HAIR TYPE
═══════════════════════════════════════════════════════════ */
export const HairTypes: AttributeItem[] = [
  { id: 30701, name: "Straight",         icon: <Zap size={14} /> },
  { id: 30702, name: "Wavy",             icon: <RefreshCw size={14} /> },
  { id: 30703, name: "Curly",            icon: <RefreshCw size={14} /> },
  { id: 30704, name: "Coily",            icon: <RefreshCw size={14} /> },
  { id: 30705, name: "Fine",             icon: <Feather size={14} /> },
  { id: 30706, name: "Thick",            icon: <Layers size={14} /> },
  { id: 30707, name: "Colour-Treated",   icon: <Palette size={14} /> },
  { id: 30708, name: "Damaged",          icon: <ShieldCheck size={14} /> },
  { id: 30709, name: "Oily Scalp",       icon: <Droplets size={14} /> },
  { id: 30710, name: "Dry & Brittle",    icon: <Leaf size={14} /> },
  { id: 30711, name: "Natural / Afro",   icon: <RefreshCw size={14} /> },
  { id: 30712, name: "All Hair Types",   icon: <Globe size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   8. HAIR CONCERN
═══════════════════════════════════════════════════════════ */
export const HairConcerns: AttributeItem[] = [
  { id: 30801, name: "Frizz Control",       icon: <ShieldCheck size={14} /> },
  { id: 30802, name: "Volume",              icon: <Layers size={14} /> },
  { id: 30803, name: "Hydration",           icon: <Droplets size={14} /> },
  { id: 30804, name: "Repair & Strengthen", icon: <Zap size={14} /> },
  { id: 30805, name: "Dandruff",            icon: <ShieldCheck size={14} /> },
  { id: 30806, name: "Scalp Health",        icon: <Leaf size={14} /> },
  { id: 30807, name: "Hair Loss",           icon: <Heart size={14} /> },
  { id: 30808, name: "Colour Protect",      icon: <Palette size={14} /> },
  { id: 30809, name: "Heat Protection",     icon: <Flame size={14} /> },
  { id: 30810, name: "Curl Definition",     icon: <RefreshCw size={14} /> },
  { id: 30811, name: "Smoothing",           icon: <Zap size={14} /> },
  { id: 30812, name: "Shine",               icon: <Sparkles size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   9. FRAGRANCE FAMILY
═══════════════════════════════════════════════════════════ */
export const FragranceFamilies: AttributeItem[] = [
  { id: 30901, name: "Floral",          icon: <Flower2 size={14} /> },
  { id: 30902, name: "Woody",           icon: <Leaf size={14} /> },
  { id: 30903, name: "Oriental",        icon: <Flame size={14} /> },
  { id: 30904, name: "Fresh",           icon: <Sun size={14} /> },
  { id: 30905, name: "Citrus",          icon: <Sun size={14} /> },
  { id: 30906, name: "Aquatic",         icon: <Droplets size={14} /> },
  { id: 30907, name: "Musky",           icon: <Moon size={14} /> },
  { id: 30908, name: "Gourmand",        icon: <Heart size={14} /> },
  { id: 30909, name: "Spicy",           icon: <Flame size={14} /> },
  { id: 30910, name: "Earthy",          icon: <Leaf size={14} /> },
  { id: 30911, name: "Powdery",         icon: <Sparkles size={14} /> },
  { id: 30912, name: "Green",           icon: <Leaf size={14} /> },
  { id: 30913, name: "Fruity",          icon: <Flower2 size={14} /> },
  { id: 30914, name: "Leather",         icon: <ShieldCheck size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   10. FRAGRANCE CONCENTRATION
═══════════════════════════════════════════════════════════ */
export const FragranceConcentrations: AttributeItem[] = [
  { id: 31001, name: "Parfum (20–30%)",            icon: <Flower2 size={14} /> },
  { id: 31002, name: "Eau de Parfum (15–20%)",     icon: <Flower2 size={14} /> },
  { id: 31003, name: "Eau de Toilette (5–15%)",    icon: <Flower2 size={14} /> },
  { id: 31004, name: "Eau de Cologne (2–4%)",      icon: <Flower2 size={14} /> },
  { id: 31005, name: "Eau Fraîche (1–3%)",         icon: <Droplets size={14} /> },
  { id: 31006, name: "Body Mist",                  icon: <Droplets size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   11. KEY INGREDIENTS
═══════════════════════════════════════════════════════════ */
export const KeyIngredients: AttributeItem[] = [
  { id: 31101, name: "Hyaluronic Acid",   icon: <Droplets size={14} /> },
  { id: 31102, name: "Retinol",           icon: <Zap size={14} /> },
  { id: 31103, name: "Vitamin C",         icon: <Sun size={14} /> },
  { id: 31104, name: "Niacinamide",       icon: <Sparkles size={14} /> },
  { id: 31105, name: "Salicylic Acid",    icon: <ShieldCheck size={14} /> },
  { id: 31106, name: "Glycolic Acid",     icon: <RefreshCw size={14} /> },
  { id: 31107, name: "Peptides",          icon: <Zap size={14} /> },
  { id: 31108, name: "Ceramides",         icon: <ShieldCheck size={14} /> },
  { id: 31109, name: "Collagen",          icon: <Sparkles size={14} /> },
  { id: 31110, name: "Squalane",          icon: <Droplets size={14} /> },
  { id: 31111, name: "Bakuchiol",         icon: <Leaf size={14} /> },
  { id: 31112, name: "Centella Asiatica", icon: <Leaf size={14} /> },
  { id: 31113, name: "Argan Oil",         icon: <Droplets size={14} /> },
  { id: 31114, name: "Rosehip",           icon: <Flower2 size={14} /> },
  { id: 31115, name: "AHA",              icon: <RefreshCw size={14} /> },
  { id: 31116, name: "BHA",              icon: <RefreshCw size={14} /> },
  { id: 31117, name: "Zinc",             icon: <ShieldCheck size={14} /> },
  { id: 31118, name: "Caffeine",         icon: <Zap size={14} /> },
  { id: 31119, name: "SPF",             icon: <Sun size={14} /> },
  { id: 31120, name: "Probiotics",       icon: <Leaf size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   12. PRODUCT FORMAT / TEXTURE
═══════════════════════════════════════════════════════════ */
export const BeautyFormats: AttributeItem[] = [
  { id: 31201, name: "Cream",         icon: <Circle size={14} /> },
  { id: 31202, name: "Gel",           icon: <Droplets size={14} /> },
  { id: 31203, name: "Serum",         icon: <Zap size={14} /> },
  { id: 31204, name: "Oil",           icon: <Droplets size={14} /> },
  { id: 31205, name: "Balm",          icon: <Heart size={14} /> },
  { id: 31206, name: "Foam",          icon: <Sparkles size={14} /> },
  { id: 31207, name: "Powder",        icon: <Sparkles size={14} /> },
  { id: 31208, name: "Stick",         icon: <Zap size={14} /> },
  { id: 31209, name: "Mist / Spray",  icon: <Droplets size={14} /> },
  { id: 31210, name: "Sheet Mask",    icon: <ShieldCheck size={14} /> },
  { id: 31211, name: "Patch",         icon: <ShieldCheck size={14} /> },
  { id: 31212, name: "Wipe",          icon: <Leaf size={14} /> },
  { id: 31213, name: "Capsule",       icon: <Circle size={14} /> },
  { id: 31214, name: "Bar",           icon: <Leaf size={14} /> },
  { id: 31215, name: "Liquid",        icon: <Droplets size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   13. SUSTAINABILITY / CERTIFICATIONS
═══════════════════════════════════════════════════════════ */
export const BeautySustainability: AttributeItem[] = [
  { id: 31301, name: "Vegan",                  icon: <Leaf size={14} /> },
  { id: 31302, name: "Cruelty Free",           icon: <Heart size={14} /> },
  { id: 31303, name: "Organic",                icon: <Leaf size={14} /> },
  { id: 31304, name: "Natural",                icon: <Flower2 size={14} /> },
  { id: 31305, name: "Clean Beauty",           icon: <ShieldCheck size={14} /> },
  { id: 31306, name: "Dermatologist Tested",   icon: <Award size={14} /> },
  { id: 31307, name: "Hypoallergenic",         icon: <ShieldCheck size={14} /> },
  { id: 31308, name: "Fragrance-Free",         icon: <ShieldCheck size={14} /> },
  { id: 31309, name: "Alcohol-Free",           icon: <ShieldCheck size={14} /> },
  { id: 31310, name: "Paraben-Free",           icon: <ShieldCheck size={14} /> },
  { id: 31311, name: "Sulphate-Free",          icon: <ShieldCheck size={14} /> },
  { id: 31312, name: "Recyclable Packaging",   icon: <Recycle size={14} /> },
  { id: 31313, name: "Refillable",             icon: <Recycle size={14} /> },
  { id: 31314, name: "Waterless",              icon: <Leaf size={14} /> },
  { id: 31315, name: "Reef-Safe",              icon: <Globe size={14} /> },
  { id: 31316, name: "Leaping Bunny",          icon: <Award size={14} /> },
  { id: 31317, name: "COSMOS Certified",       icon: <Award size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   14. SPF LEVEL
═══════════════════════════════════════════════════════════ */
export const SpfLevels: AttributeItem[] = [
  { id: 31401, name: "SPF 15",  icon: <Sun size={14} /> },
  { id: 31402, name: "SPF 20",  icon: <Sun size={14} /> },
  { id: 31403, name: "SPF 30",  icon: <Sun size={14} /> },
  { id: 31404, name: "SPF 40",  icon: <Sun size={14} /> },
  { id: 31405, name: "SPF 50",  icon: <Sun size={14} /> },
  { id: 31406, name: "SPF 50+", icon: <Sun size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   15. PRODUCT SIZE / VOLUME
═══════════════════════════════════════════════════════════ */
export const BeautySizes: AttributeItem[] = [
  { id: 31501, name: "Travel (< 50ml)",   icon: <Tag size={14} /> },
  { id: 31502, name: "Mini (50–75ml)",    icon: <Tag size={14} /> },
  { id: 31503, name: "Standard (100ml)",  icon: <Tag size={14} /> },
  { id: 31504, name: "Medium (150ml)",    icon: <Tag size={14} /> },
  { id: 31505, name: "Large (200ml+)",    icon: <Tag size={14} /> },
  { id: 31506, name: "XL (500ml+)",       icon: <Tag size={14} /> },
  { id: 31507, name: "Refill Size",       icon: <Recycle size={14} /> },
  { id: 31508, name: "Set / Kit",         icon: <Crown size={14} /> },
];

/* ═══════════════════════════════════════════════════════════
   16. BEAUTY BRANDS
═══════════════════════════════════════════════════════════ */
export const BeautyBrands: AttributeItem[] = [
  // Luxury / Prestige
  { id: 31601, name: "La Mer",                icon: <Crown size={14} /> },
  { id: 31602, name: "La Prairie",            icon: <Crown size={14} /> },
  { id: 31603, name: "Sisley",                icon: <Crown size={14} /> },
  { id: 31604, name: "Chanel Beauty",         icon: <Crown size={14} /> },
  { id: 31605, name: "Dior Beauty",           icon: <Crown size={14} /> },
  { id: 31606, name: "Giorgio Armani Beauty", icon: <Crown size={14} /> },
  { id: 31607, name: "Yves Saint Laurent Beauty", icon: <Crown size={14} /> },
  { id: 31608, name: "Tom Ford Beauty",       icon: <Crown size={14} /> },
  { id: 31609, name: "Guerlain",              icon: <Crown size={14} /> },
  { id: 31610, name: "Charlotte Tilbury",     icon: <Star size={14} /> },
  // Premium
  { id: 31611, name: "NARS",                  icon: <Star size={14} /> },
  { id: 31612, name: "MAC Cosmetics",         icon: <Star size={14} /> },
  { id: 31613, name: "Urban Decay",           icon: <Star size={14} /> },
  { id: 31614, name: "Too Faced",             icon: <Star size={14} /> },
  { id: 31615, name: "Pat McGrath",           icon: <Star size={14} /> },
  { id: 31616, name: "Fenty Beauty",          icon: <Star size={14} /> },
  { id: 31617, name: "Rare Beauty",           icon: <Star size={14} /> },
  { id: 31618, name: "Hourglass",             icon: <Star size={14} /> },
  // Skincare specialist
  { id: 31619, name: "The Ordinary",          icon: <Droplets size={14} /> },
  { id: 31620, name: "CeraVe",               icon: <Droplets size={14} /> },
  { id: 31621, name: "Drunk Elephant",        icon: <Droplets size={14} /> },
  { id: 31622, name: "Paula's Choice",        icon: <Droplets size={14} /> },
  { id: 31623, name: "Dr. Barbara Sturm",     icon: <Droplets size={14} /> },
  { id: 31624, name: "Tatcha",               icon: <Droplets size={14} /> },
  { id: 31625, name: "Glow Recipe",          icon: <Sparkles size={14} /> },
  { id: 31626, name: "COSRX",               icon: <Droplets size={14} /> },
  { id: 31627, name: "Augustinus Bader",     icon: <Crown size={14} /> },
  // Haircare specialist
  { id: 31628, name: "Olaplex",             icon: <Zap size={14} /> },
  { id: 31629, name: "GHD",                icon: <Zap size={14} /> },
  { id: 31630, name: "Dyson Hair",          icon: <Zap size={14} /> },
  { id: 31631, name: "Kérastase",           icon: <Sparkles size={14} /> },
  { id: 31632, name: "Moroccanoil",         icon: <Droplets size={14} /> },
  { id: 31633, name: "Living Proof",        icon: <Leaf size={14} /> },
  // Fragrance
  { id: 31634, name: "Jo Malone",           icon: <Flower2 size={14} /> },
  { id: 31635, name: "Diptyque",            icon: <Flower2 size={14} /> },
  { id: 31636, name: "Maison Margiela",     icon: <Flower2 size={14} /> },
  { id: 31637, name: "Byredo",             icon: <Flower2 size={14} /> },
  { id: 31638, name: "Le Labo",            icon: <Flower2 size={14} /> },
  // Clean / Natural
  { id: 31639, name: "Herbivore",          icon: <Leaf size={14} /> },
  { id: 31640, name: "Tata Harper",        icon: <Leaf size={14} /> },
  { id: 31641, name: "RMS Beauty",         icon: <Leaf size={14} /> },
  { id: 31642, name: "Ilia Beauty",        icon: <Leaf size={14} /> },
  { id: 31643, name: "Ere Perez",          icon: <Leaf size={14} /> },
];

/* ======================================================
   📏 SIZE (GROUP 20001 | VALUES 20001xxx)
   ====================================================== */
export const fashionSizeAttributes: AttributeItem = {
  id: 20001,
  name: "Size",
  values: [
    /* ───────── Adult ───────── */
    { id: 20001001, name: "One-Size", icon: <Ruler size={14} /> },
    { id: 20001002, name: "XXS", icon: <Ruler size={14} /> },
    { id: 20001003, name: "XS", icon: <Ruler size={14} /> },
    { id: 20001004, name: "S", icon: <Ruler size={14} /> },
    { id: 20001005, name: "M", icon: <Ruler size={14} /> },
    { id: 20001006, name: "L", icon: <Ruler size={14} /> },
    { id: 20001007, name: "XL", icon: <Ruler size={14} /> },
    { id: 20001008, name: "XXL", icon: <Ruler size={14} /> },
    { id: 20001009, name: "XXXL", icon: <Ruler size={14} /> },
    { id: 20001010, name: "XXXXL", icon: <Ruler size={14} /> },
    { id: 20001011, name: "1XL", icon: <Ruler size={14} /> },
    { id: 20001012, name: "2XL", icon: <Ruler size={14} /> },
    { id: 20001013, name: "3XL", icon: <Ruler size={14} /> },
    { id: 20001014, name: "4XL", icon: <Ruler size={14} /> },
    { id: 20001015, name: "5XL", icon: <Ruler size={14} /> },

    /* ───────── Petite / Tall ───────── */
    { id: 20001016, name: "Petite XS", icon: <Ruler size={14} /> },
    { id: 20001017, name: "Petite S", icon: <Ruler size={14} /> },
    { id: 20001018, name: "Petite M", icon: <Ruler size={14} /> },
    { id: 20001019, name: "Petite L", icon: <Ruler size={14} /> },
    { id: 20001020, name: "Tall S", icon: <Ruler size={14} /> },
    { id: 20001021, name: "Tall M", icon: <Ruler size={14} /> },
    { id: 20001022, name: "Tall L", icon: <Ruler size={14} /> },

    /* ───────── Baby / Toddler ───────── */
    { id: 20001030, name: "0–3 Months", icon: <Baby size={14} /> },
    { id: 20001031, name: "3–6 Months", icon: <Baby size={14} /> },
    { id: 20001032, name: "6–9 Months", icon: <Baby size={14} /> },
    { id: 20001033, name: "9–12 Months", icon: <Baby size={14} /> },
    { id: 20001034, name: "12–18 Months", icon: <Baby size={14} /> },
    { id: 20001035, name: "18–24 Months", icon: <Baby size={14} /> },

    /* ───────── Kids ───────── */
    { id: 20001036, name: "2–3 Years", icon: <Baby size={14} /> },
    { id: 20001037, name: "3–4 Years", icon: <Baby size={14} /> },
    { id: 20001038, name: "4–5 Years", icon: <Baby size={14} /> },
    { id: 20001039, name: "5–6 Years", icon: <Baby size={14} /> },
    { id: 20001040, name: "6–7 Years", icon: <Baby size={14} /> },
    { id: 20001041, name: "7–8 Years", icon: <Baby size={14} /> },
    { id: 20001042, name: "8–9 Years", icon: <Baby size={14} /> },
    { id: 20001043, name: "9–10 Years", icon: <Baby size={14} /> },
    { id: 20001044, name: "10–11 Years", icon: <Baby size={14} /> },
    { id: 20001045, name: "11–12 Years", icon: <Baby size={14} /> },
  ],
};

/* ======================================================
   🧵 MATERIAL (GROUP 20003 | VALUES 20003xxx)
   ====================================================== */
export const fashionMaterialAttributes: AttributeItem = {
  id: 20003,
  name: "Material",
  values: [
    { id: 20003001, name: "Linen" },
    { id: 20003002, name: "Flannelette" },
    { id: 20003003, name: "Cotton" },
    { id: 20003004, name: "Chiffon" },
    { id: 20003005, name: "Tweed" },
    { id: 20003006, name: "Wool-Like Fabric" },
    { id: 20003007, name: "Knitwear" },
    { id: 20003008, name: "Woven Fabric" },
    { id: 20003009, name: "Lace" },
    { id: 20003010, name: "Velvet" },
    { id: 20003011, name: "Polyamide" },
    { id: 20003012, name: "Polyurethane (PU)" },
    { id: 20003013, name: "Denim" },
    { id: 20003014, name: "Corduroy" },
    { id: 20003015, name: "Coated Fabric" },
    { id: 20003016, name: "Swimsuit Fabric" },
    { id: 20003017, name: "Canvas" },
    { id: 20003018, name: "Mesh Fabric" },
    { id: 20003019, name: "Wool" },
    { id: 20003020, name: "Suede" },
    { id: 20003021, name: "Glitter" },
  ],
};

/* ======================================================
   📦 EXPORT (FACET ORDER IS STABLE)
   ====================================================== */
/* ──────────────────────────────────────────────
   ATTRIBUTE IDS (SAFE + STABLE)
────────────────────────────────────────────── */

export const GENERAL_COLOR_ATTRIBUTE_ID = 90001;

/* ──────────────────────────────────────────────
   PRODUCTS ATTRIBUTES (single unified colour swatch
   list, shared by every category — Fashion, Beauty,
   Electronics, etc. There is intentionally only one
   Color attribute group in this file.)
────────────────────────────────────────────── */

export const productColorAttributes: AttributeItem = {
  id: GENERAL_COLOR_ATTRIBUTE_ID,
  name: "Color",
  values: [
    /* ───── NEUTRALS ───── */
    { id: 9000110, name: "Off White",     hexCodes: ["#F8F8F0"] },
    { id: 9000111, name: "Pearl",         hexCodes: ["#EAE0C8"] },
    { id: 9000112, name: "Stone",         hexCodes: ["#B8B5A9"] },
    { id: 9000113, name: "Taupe",         hexCodes: ["#483C32"] },
    { id: 9000114, name: "Sand",          hexCodes: ["#C2B280"] },
    { id: 9000115, name: "Khaki",         hexCodes: ["#C3B091"] },
    { id: 9000116, name: "Camel",         hexCodes: ["#C19A6B"] },
    { id: 9000117, name: "Mocha",         hexCodes: ["#967969"] },
    { id: 9000118, name: "Chocolate",     hexCodes: ["#7B3F00"] },
    { id: 9000119, name: "Espresso",      hexCodes: ["#4B3621"] },

    /* ───── BLACK & WHITE / GRAYSCALE ───── */
    { id: 9001101, name: "Black",         hexCodes: ["#000000"] },
    { id: 9001102, name: "Licorice",      hexCodes: ["#0C0C0C"] },
    { id: 9001103, name: "Jet Black",     hexCodes: ["#191919"] },
    { id: 9001104, name: "Graphite",      hexCodes: ["#262626"] },
    { id: 9001105, name: "Charcoal",      hexCodes: ["#323232"] },
    { id: 9001106, name: "Dark Gray",     hexCodes: ["#4C4C4C"] },
    { id: 9001107, name: "Ash Gray",      hexCodes: ["#666666"] },
    { id: 9001108, name: "Medium Gray",   hexCodes: ["#7F7F7F"] },
    { id: 9001109, name: "Silver Gray",   hexCodes: ["#999999"] },
    { id: 9001110, name: "Light Gray",    hexCodes: ["#B2B2B2"] },
    { id: 9001111, name: "Gainsboro",     hexCodes: ["#CCCCCC"] },
    { id: 9001112, name: "Smoke",         hexCodes: ["#E5E5E5"] },
    { id: 9001113, name: "White",         hexCodes: ["#FFFFFF"] },

    /* ───── REDS ───── */
    { id: 9000401, name: "Pure Red",       hexCodes: ["#FF0000"] },
    { id: 9000402, name: "Vivid Red",      hexCodes: ["#E60000"] },
    { id: 9000403, name: "True Red",       hexCodes: ["#CF0101"] },
    { id: 9000404, name: "Bold Red",       hexCodes: ["#C00000"] },
    { id: 9000405, name: "Dark Red",       hexCodes: ["#A60202"] },
    { id: 9000416, name: "Light Coral",    hexCodes: ["#FF8B8E"] },
    { id: 9000417, name: "Neon Red",       hexCodes: ["#FF3D41"] },
    { id: 9000418, name: "Firetruck Red",  hexCodes: ["#DC0005"] },
    { id: 9000419, name: "Deep Red",       hexCodes: ["#C80004"] },
    { id: 9000420, name: "Maroon",         hexCodes: ["#B40000"] },
    { id: 9000421, name: "Burgundy",       hexCodes: ["#690000"] },
    { id: 9000422, name: "Flame Red",      hexCodes: ["#E31919"] },
    { id: 9000423, name: "Vermilion",      hexCodes: ["#EA2300"] },
    { id: 9000424, name: "Ruby Red",       hexCodes: ["#DD1212"] },
    { id: 9000425, name: "Bright Red",     hexCodes: ["#F02525"] },
    { id: 9000426, name: "Hot Red",        hexCodes: ["#EA0A0A"] },
    { id: 9000427, name: "Candy Red",      hexCodes: ["#DD1717"] },
    { id: 9000428, name: "Tomato Red",     hexCodes: ["#FB3310"] },
    { id: 9000429, name: "Rose Red",       hexCodes: ["#F40D30"] },
    { id: 9000430, name: "Cardinal",       hexCodes: ["#C70E0E"] },
    { id: 9000431, name: "Raspberry",      hexCodes: ["#D6204E"] },
    { id: 9000406, name: "Crimson",        hexCodes: ["#DC143C"] },
    { id: 9000407, name: "Scarlet",        hexCodes: ["#FF2400"] },
    { id: 9000408, name: "Cherry Red",     hexCodes: ["#D2042D"] },
    { id: 9000409, name: "Wine",           hexCodes: ["#722F37"] },
    { id: 9000410, name: "Coral",          hexCodes: ["#FF7F50"] },
    { id: 9000411, name: "Salmon",         hexCodes: ["#FA8072"] },
    { id: 9000412, name: "Blush Pink",     hexCodes: ["#F4C2C2"] },
    { id: 9000413, name: "Hot Pink",       hexCodes: ["#FF69B4"] },
    { id: 9000414, name: "Fuchsia",        hexCodes: ["#FF00FF"] },
    { id: 9000415, name: "Magenta",        hexCodes: ["#D1007A"] },

    /* ───── PINKS ───── */
    { id: 9001201, name: "Electric Pink",  hexCodes: ["#FB00BE"] },
    { id: 9001202, name: "Neon Pink",      hexCodes: ["#FF0074"] },
    { id: 9001203, name: "Bright Pink",    hexCodes: ["#FF00CE"] },
    { id: 9001204, name: "Deep Pink",      hexCodes: ["#FF07A9"] },
    { id: 9001205, name: "Cerise",         hexCodes: ["#EE006C"] },

    /* ───── BERRY ───── */
    { id: 9001301, name: "Plum Berry",     hexCodes: ["#990F4B"] },
    { id: 9001302, name: "Mulberry",       hexCodes: ["#AD3D5E"] },
    { id: 9001303, name: "Dusty Rose",     hexCodes: ["#C05F72"] },
    { id: 9001304, name: "Mauve Pink",     hexCodes: ["#D27F87"] },
    { id: 9001305, name: "Blush Berry",    hexCodes: ["#E39E9C"] },

    /* ───── ORANGES & YELLOWS ───── */
    { id: 9000505, name: "Peach",          hexCodes: ["#FFE5B4"] },
    { id: 9000506, name: "Apricot",        hexCodes: ["#FBCEB1"] },
    { id: 9000507, name: "Tangerine",      hexCodes: ["#F28500"] },
    { id: 9000508, name: "Burnt Orange",   hexCodes: ["#CC5500"] },
    { id: 9000509, name: "Rust",           hexCodes: ["#B7410E"] },
    { id: 9000510, name: "Golden Yellow",  hexCodes: ["#FFDF00"] },
    { id: 9000511, name: "Lemon",          hexCodes: ["#FFF44F"] },
    { id: 9000512, name: "Canary Yellow",  hexCodes: ["#FFEF00"] },
    { id: 9000513, name: "Sunflower",      hexCodes: ["#FFC512"] },

    /* ───── ORANGES (EXTENDED) ───── */
    { id: 9001401, name: "Burnt Sienna",   hexCodes: ["#C65102"] },
    { id: 9001402, name: "Amber Orange",   hexCodes: ["#D56602"] },
    { id: 9001403, name: "Dark Amber",     hexCodes: ["#E37B01"] },
    { id: 9001404, name: "Deep Amber",     hexCodes: ["#F19001"] },
    { id: 9001405, name: "Orange",         hexCodes: ["#FFA500"] },
    { id: 9001406, name: "Pale Orange",    hexCodes: ["#FDCC99"] },
    { id: 9001407, name: "Warm Orange",    hexCodes: ["#FB9932"] },
    { id: 9001408, name: "Pumpkin",        hexCodes: ["#FA8000"] },
    { id: 9001409, name: "Bronze Orange",  hexCodes: ["#C86600"] },
    { id: 9001410, name: "Dark Pumpkin",   hexCodes: ["#7D4000"] },

    /* ───── YELLOWS (EXTENDED) ───── */
    { id: 9001501, name: "Pale Yellow",    hexCodes: ["#FCF5B2"] },
    { id: 9001502, name: "Soft Yellow",    hexCodes: ["#FBEE7F"] },
    { id: 9001503, name: "Bright Yellow",  hexCodes: ["#F9E432"] },
    { id: 9001504, name: "Pure Yellow",    hexCodes: ["#F8DE00"] },
    { id: 9001505, name: "Gold Yellow",    hexCodes: ["#DFC700"] },
    { id: 9001506, name: "Cream Yellow",   hexCodes: ["#FFFBA0"] },
    { id: 9001507, name: "Butter",         hexCodes: ["#FFEC8E"] },
    { id: 9001508, name: "Straw",          hexCodes: ["#FEE283"] },
    { id: 9001509, name: "Honey",          hexCodes: ["#F6D484"] },
    { id: 9001510, name: "Warm Honey",     hexCodes: ["#EFC576"] },

    /* ───── GREENS ───── */
    { id: 9000305, name: "Emerald",        hexCodes: ["#50C878"] },
    { id: 9000306, name: "Jade",           hexCodes: ["#00A86B"] },
    { id: 9000307, name: "Sage",           hexCodes: ["#9CAF88"] },
    { id: 9000308, name: "Moss Green",     hexCodes: ["#8A9A5B"] },
    { id: 9000309, name: "Army Green",     hexCodes: ["#4B5320"] },
    { id: 9000310, name: "Pistachio",      hexCodes: ["#93C572"] },
    { id: 9000311, name: "Sea Green",      hexCodes: ["#2E8B57"] },
    { id: 9000312, name: "Kelly Green",    hexCodes: ["#4CBB17"] },
    { id: 9000313, name: "Neon Green",     hexCodes: ["#39FF14"] },
    { id: 9000314, name: "Dark Green",     hexCodes: ["#006400"] },

    /* ───── GREENS (EXTENDED) ───── */
    { id: 9001601, name: "Mint",           hexCodes: ["#CDF5D2"] },
    { id: 9001602, name: "Spring Green",   hexCodes: ["#69E279"] },
    { id: 9001603, name: "Vivid Green",    hexCodes: ["#04A519"] },
    { id: 9001604, name: "Forest Green",   hexCodes: ["#049016"] },
    { id: 9001605, name: "Deep Forest",    hexCodes: ["#013E09"] },
    { id: 9001606, name: "Lime Green",     hexCodes: ["#00FF00"] },
    { id: 9001607, name: "Bright Lime",    hexCodes: ["#05C800"] },
    { id: 9001608, name: "Grass Green",    hexCodes: ["#069300"] },
    { id: 9001609, name: "Fern",           hexCodes: ["#056200"] },
    { id: 9001610, name: "Pine",           hexCodes: ["#033500"] },
    { id: 9001611, name: "Laurel",         hexCodes: ["#AAD688"] },
    { id: 9001612, name: "Artichoke",      hexCodes: ["#98C377"] },
    { id: 9001613, name: "Herb Green",     hexCodes: ["#8BBD78"] },
    { id: 9001614, name: "Leaf Green",     hexCodes: ["#5EA758"] },
    { id: 9001615, name: "Dark Sage",      hexCodes: ["#47894B"] },
    { id: 9001616, name: "Neon Lime",      hexCodes: ["#76FF7B"] },
    { id: 9001617, name: "Lime Punch",     hexCodes: ["#A1FF7B"] },
    { id: 9001618, name: "Green Yellow",   hexCodes: ["#C4FF7B"] },
    { id: 9001619, name: "Yellow Lime",    hexCodes: ["#E3FF7A"] },
    { id: 9001620, name: "Lemon Lime",     hexCodes: ["#FFFE7A"] },

    /* ───── BLUES ───── */
    { id: 9000206, name: "Baby Blue",      hexCodes: ["#89CFF0"] },
    { id: 9000207, name: "Powder Blue",    hexCodes: ["#B0E0E6"] },
    { id: 9000208, name: "Steel Blue",     hexCodes: ["#4682B4"] },
    { id: 9000209, name: "Denim Blue",     hexCodes: ["#1560BD"] },
    { id: 9000210, name: "Cobalt Blue",    hexCodes: ["#0047AB"] },
    { id: 9000211, name: "Sapphire",       hexCodes: ["#0F52BA"] },
    { id: 9000212, name: "Aqua",           hexCodes: ["#00FFFF"] },
    { id: 9000213, name: "Cyan",           hexCodes: ["#00BCD4"] },
    { id: 9000214, name: "Petrol Blue",    hexCodes: ["#005F6A"] },
    { id: 9000215, name: "Ice Blue",       hexCodes: ["#D6F5FF"] },

    /* ───── BLUES (EXTENDED) ───── */
    { id: 9001701, name: "Midnight Blue",  hexCodes: ["#040273"] },
    { id: 9001702, name: "Navy",           hexCodes: ["#022477"] },
    { id: 9001703, name: "Royal Navy",     hexCodes: ["#0E3879"] },
    { id: 9001704, name: "Dark Blue",      hexCodes: ["#1F4A7C"] },
    { id: 9001705, name: "Ocean Blue",     hexCodes: ["#315B7D"] },
    { id: 9001706, name: "Cornflower",     hexCodes: ["#5170D7"] },
    { id: 9001707, name: "Periwinkle",     hexCodes: ["#6E8CDC"] },
    { id: 9001708, name: "Muted Blue",     hexCodes: ["#8DA6DF"] },
    { id: 9001709, name: "Sky Blue",       hexCodes: ["#1A84B8"] },
    { id: 9001710, name: "Azure",          hexCodes: ["#1A8CB8"] },
    { id: 9001711, name: "Cerulean",       hexCodes: ["#1A94B8"] },
    { id: 9001712, name: "Peacock Blue",   hexCodes: ["#1A9CB8"] },
    { id: 9001713, name: "Lagoon Blue",    hexCodes: ["#1AA4B8"] },

    /* ───── BLUE GRAY ───── */
    { id: 9001801, name: "Slate Blue",     hexCodes: ["#758DA3"] },
    { id: 9001802, name: "Storm Blue",     hexCodes: ["#859CB2"] },
    { id: 9001803, name: "Dusty Blue",     hexCodes: ["#95ABC2"] },
    { id: 9001804, name: "Glacier Blue",   hexCodes: ["#A6BAD2"] },
    { id: 9001805, name: "Pale Steel",     hexCodes: ["#B7C9E2"] },

    /* ───── DENIM ───── */
    { id: 9001901, name: "Dark Denim",     hexCodes: ["#071F35"] },
    { id: 9001902, name: "Indigo Denim",   hexCodes: ["#24496B"] },
    { id: 9001903, name: "Medium Denim",   hexCodes: ["#446D92"] },
    { id: 9001904, name: "Faded Denim",    hexCodes: ["#98B1C8"] },
    { id: 9001905, name: "Washed Denim",   hexCodes: ["#F7FAFE"] },

    /* ───── AQUA BLUE ───── */
    { id: 9002001, name: "Soft Aqua",      hexCodes: ["#90C0E0"] },
    { id: 9002002, name: "Light Aqua",     hexCodes: ["#80B8D8"] },
    { id: 9002003, name: "Vivid Aqua",     hexCodes: ["#3890C8"] },
    { id: 9002004, name: "Bright Aqua",    hexCodes: ["#60A8D0"] },
    { id: 9002005, name: "Pale Aqua",      hexCodes: ["#98C8E0"] },

    /* ───── CYAN ───── */
    { id: 9002101, name: "Electric Cyan",  hexCodes: ["#00FFFF"] },
    { id: 9002102, name: "Bright Cyan",    hexCodes: ["#00E1E1"] },
    { id: 9002103, name: "Medium Cyan",    hexCodes: ["#00C3C3"] },
    { id: 9002104, name: "Deep Cyan",      hexCodes: ["#00A7A7"] },
    { id: 9002105, name: "Dark Teal",      hexCodes: ["#008B8B"] },

    /* ───── PURPLES ───── */
    { id: 9000604, name: "Violet",         hexCodes: ["#8F00FF"] },
    { id: 9000605, name: "Lilac",          hexCodes: ["#C8A2C8"] },
    { id: 9000606, name: "Mauve",          hexCodes: ["#E0B0FF"] },
    { id: 9000607, name: "Orchid",         hexCodes: ["#DA70D6"] },
    { id: 9000608, name: "Eggplant",       hexCodes: ["#614051"] },
    { id: 9000609, name: "Deep Purple",    hexCodes: ["#512DA8"] },

    /* ───── PURPLES (EXTENDED) ───── */
    { id: 9002201, name: "Electric Purple", hexCodes: ["#B700FF"] },
    { id: 9002202, name: "Vivid Purple",   hexCodes: ["#9A00D6"] },
    { id: 9002203, name: "Grape",          hexCodes: ["#7900A8"] },
    { id: 9002204, name: "Dark Grape",     hexCodes: ["#600085"] },
    { id: 9002205, name: "Blackberry",     hexCodes: ["#300042"] },

    /* ───── VIOLET / LAVENDER ───── */
    { id: 9002301, name: "Soft Violet",    hexCodes: ["#D6B4FC"] },
    { id: 9002302, name: "Wisteria",       hexCodes: ["#E0BCF5"] },
    { id: 9002303, name: "Lavender",       hexCodes: ["#EAC3EE"] },
    { id: 9002304, name: "Pale Lavender",  hexCodes: ["#F4CAE7"] },
    { id: 9002305, name: "Blush Lavender", hexCodes: ["#FFD1DF"] },

    /* ───── INDIGO ───── */
    { id: 9002401, name: "Midnight Indigo", hexCodes: ["#191970"] },
    { id: 9002402, name: "Royal Indigo",   hexCodes: ["#002395"] },
    { id: 9002403, name: "Dark Indigo",    hexCodes: ["#00416A"] },
    { id: 9002404, name: "Indigo Blue",    hexCodes: ["#1560BD"] },
    { id: 9002405, name: "Soft Indigo",    hexCodes: ["#5D76CB"] },

    /* ───── TRUE COLORS / HERITAGE ───── */
    { id: 9002501, name: "Deep Crimson",   hexCodes: ["#830000"] },
    { id: 9002502, name: "Dark Sienna",    hexCodes: ["#8B4D00"] },
    { id: 9002503, name: "Olive Drab",     hexCodes: ["#666700"] },
    { id: 9002504, name: "Hunter Green",   hexCodes: ["#00510E"] },
    { id: 9002505, name: "Midnight Navy",  hexCodes: ["#000440"] },

    /* ───── BROWNS ───── */
    { id: 9000901, name: "Tan",            hexCodes: ["#D2B48C"] },
    { id: 9000902, name: "Chestnut",       hexCodes: ["#954535"] },
    { id: 9000903, name: "Walnut",         hexCodes: ["#5D432C"] },
    { id: 9000904, name: "Mahogany",       hexCodes: ["#C04000"] },
    { id: 9000905, name: "Cocoa",          hexCodes: ["#6F4E37"] },

    /* ───── BROWNS (EXTENDED) ───── */
    { id: 9002601, name: "Sandy Brown",    hexCodes: ["#FA9755"] },
    { id: 9002602, name: "Warm Brown",     hexCodes: ["#C77844"] },
    { id: 9002603, name: "Hazel",          hexCodes: ["#87522E"] },
    { id: 9002604, name: "Umber",          hexCodes: ["#7B4B2A"] },
    { id: 9002605, name: "Toffee",         hexCodes: ["#613B21"] },
    { id: 9002606, name: "Dark Toffee",    hexCodes: ["#553311"] },
    { id: 9002607, name: "Auburn Brown",   hexCodes: ["#664433"] },
    { id: 9002608, name: "Seal Brown",     hexCodes: ["#332211"] },
    { id: 9002609, name: "Caramel",        hexCodes: ["#996633"] },
    { id: 9002610, name: "Warm Caramel",   hexCodes: ["#CCAA66"] },
    { id: 9002611, name: "Ginger",         hexCodes: ["#CC8833"] },
    { id: 9002612, name: "Cognac",         hexCodes: ["#D08C42"] },
    { id: 9002613, name: "Whiskey",        hexCodes: ["#D4914F"] },
    { id: 9002614, name: "Teak",           hexCodes: ["#D9955B"] },
    { id: 9002615, name: "Butterscotch",   hexCodes: ["#DD9966"] },

    /* ───── CHESTNUTS ───── */
    { id: 9002701, name: "Dark Chestnut",  hexCodes: ["#742802"] },
    { id: 9002702, name: "Russet",         hexCodes: ["#8F4934"] },
    { id: 9002703, name: "Clay",           hexCodes: ["#A96A5C"] },
    { id: 9002704, name: "Rosy Brown",     hexCodes: ["#C38B85"] },
    { id: 9002705, name: "Pale Chestnut",  hexCodes: ["#DDADAF"] },

    /* ───── METALLICS ───── */
    { id: 9000701, name: "Gold",           hexCodes: ["#D4AF37"] },
    { id: 9000702, name: "Rose Gold",      hexCodes: ["#B76E79"] },
    { id: 9000703, name: "Copper",         hexCodes: ["#B87333"] },
    { id: 9000704, name: "Bronze",         hexCodes: ["#CD7F32"] },
    { id: 9000705, name: "Gunmetal",       hexCodes: ["#2A3439"] },
    { id: 9000706, name: "Titanium",       hexCodes: ["#878681"] },
    { id: 9000707, name: "Platinum",       hexCodes: ["#E5E4E2"] },
    { id: 9000708, name: "Chrome",         hexCodes: ["#D7D7D7"] },

    /* ───── PASTELS ───── */
    { id: 9001001, name: "Pastel Blue",    hexCodes: ["#AEC6CF"] },
    { id: 9001002, name: "Pastel Green",   hexCodes: ["#77DD77"] },
    { id: 9001003, name: "Pastel Pink",    hexCodes: ["#FFD1DC"] },
    { id: 9001004, name: "Pastel Yellow",  hexCodes: ["#FDFD96"] },
    { id: 9001005, name: "Pastel Purple",  hexCodes: ["#C3B1E1"] },

    /* ───── SPECIAL / MIXED ───── */
    { id: 9000801, name: "Multicolor" },
    { id: 9000802, name: "Transparent" },
    { id: 9000803, name: "Matte Black",    hexCodes: ["#1C1C1C"] },
    { id: 9000804, name: "Glossy White",   hexCodes: ["#FAFAFA"] },

    /* ───── SPECIAL EFFECTS ───── */
    { id: 9000805, name: "Rainbow" },
    { id: 9000806, name: "Iridescent" },
    { id: 9000807, name: "Holographic" },
    { id: 9000808, name: "Metallic" },
    { id: 9000809, name: "Glow in the Dark" },
    { id: 9000810, name: "Clear",          hexCodes: ["#FFFFFF"] },
  ],
};

/** Kept as an array for backward compatibility with existing call sites
 *  that expect `productColors` to be a list of colour groups. */
export const productColors: AttributeItem[] = [productColorAttributes];

export const fashionAttributes: AttributeItem[] = [
  fashionSizeAttributes,
  productColorAttributes,
  fashionMaterialAttributes,
];


/* ═══════════════════════════════════════════════════════════════════════
   ⚡ ELECTRONICS ATTRIBUTES
   Rebuilt so device-specific facets (screen size, resolution, display
   type, RAM/storage) are scoped per device via `appliesTo`, instead of
   one shared bucket where e.g. laptop inches could leak into phones.
═══════════════════════════════════════════════════════════════════════ */

/** Subcategory ids these attribute groups apply to. Match these to
 *  whatever subcategory ids your Electronics category tree actually uses. */
export const ELECTRONICS_CATEGORY_IDS = {
  TV: 301,
  PHONE: 101,
  LAPTOP: 102,
  TABLET: 103,
  SMARTWATCH: 104,
  CAMERA: 105,
  HEADPHONES: 106,
  GAMING_CONSOLE: 107,
} as const;

/* ──────────────────────────────────────────────
   📺 TV — screen size / resolution / display / smart
────────────────────────────────────────────── */
export const tvScreenSizeAttributes: AttributeItem = {
  id: 30010,
  name: "Screen Size",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.TV],
  values: [
    { id: 3001001, name: '32"', numericValue: 32, unit: "inch" },
    { id: 3001002, name: '40"', numericValue: 40, unit: "inch" },
    { id: 3001003, name: '43"', numericValue: 43, unit: "inch" },
    { id: 3001004, name: '50"', numericValue: 50, unit: "inch" },
    { id: 3001005, name: '55"', numericValue: 55, unit: "inch" },
    { id: 3001006, name: '65"', numericValue: 65, unit: "inch" },
    { id: 3001007, name: '75"', numericValue: 75, unit: "inch" },
    { id: 3001008, name: '85"', numericValue: 85, unit: "inch" },
    { id: 3001009, name: '98"+', numericValue: 98, unit: "inch" },
  ],
};

export const tvResolutionAttributes: AttributeItem = {
  id: 30011,
  name: "Resolution",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.TV],
  values: [
    { id: 3001101, name: "HD" },
    { id: 3001102, name: "Full HD" },
    { id: 3001103, name: "4K UHD" },
    { id: 3001104, name: "8K" },
  ],
};

export const tvDisplayTypeAttributes: AttributeItem = {
  id: 30012,
  name: "Display Type",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.TV],
  values: [
    { id: 3001201, name: "LED" },
    { id: 3001202, name: "OLED" },
    { id: 3001203, name: "QLED" },
    { id: 3001204, name: "Neo QLED" },
    { id: 3001205, name: "Mini LED" },
    { id: 3001206, name: "Micro LED" },
  ],
};

export const tvSmartAttributes: AttributeItem = {
  id: 30013,
  name: "Smart TV",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.TV],
  values: [
    { id: 3001301, name: "Yes" },
    { id: 3001302, name: "No" },
  ],
};

/* ──────────────────────────────────────────────
   📱 PHONE — screen size / resolution / display / storage / RAM
────────────────────────────────────────────── */
export const phoneScreenSizeAttributes: AttributeItem = {
  id: 30020,
  name: "Screen Size",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.PHONE],
  values: [
    { id: 3002001, name: "6.1 inch", numericValue: 6.1, unit: "inch" },
    { id: 3002002, name: "6.3 inch", numericValue: 6.3, unit: "inch" },
    { id: 3002003, name: "6.5 inch", numericValue: 6.5, unit: "inch" },
    { id: 3002004, name: "6.7 inch", numericValue: 6.7, unit: "inch" },
    { id: 3002005, name: "6.9 inch", numericValue: 6.9, unit: "inch" },
  ],
};

export const phoneResolutionAttributes: AttributeItem = {
  id: 30021,
  name: "Resolution",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.PHONE],
  values: [
    { id: 3002101, name: "HD+" },
    { id: 3002102, name: "Full HD+" },
    { id: 3002103, name: "Quad HD+" },
    { id: 3002104, name: "2K" },
  ],
};

export const phoneDisplayTypeAttributes: AttributeItem = {
  id: 30022,
  name: "Display Type",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.PHONE],
  values: [
    { id: 3002201, name: "IPS LCD" },
    { id: 3002202, name: "OLED" },
    { id: 3002203, name: "AMOLED" },
    { id: 3002204, name: "Super AMOLED" },
    { id: 3002205, name: "Retina" },
    { id: 3002206, name: "ProMotion (120Hz)" },
  ],
};

export const phoneStorageAttributes: AttributeItem = {
  id: 30023,
  name: "Storage",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.PHONE],
  values: [
    { id: 3002301, name: "32 GB" },
    { id: 3002302, name: "64 GB" },
    { id: 3002303, name: "128 GB" },
    { id: 3002304, name: "256 GB" },
    { id: 3002305, name: "512 GB" },
    { id: 3002306, name: "1 TB" },
  ],
};

export const phoneRamAttributes: AttributeItem = {
  id: 30024,
  name: "RAM",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.PHONE],
  values: [
    { id: 3002401, name: "2 GB" },
    { id: 3002402, name: "4 GB" },
    { id: 3002403, name: "6 GB" },
    { id: 3002404, name: "8 GB" },
    { id: 3002405, name: "12 GB" },
    { id: 3002406, name: "16 GB" },
  ],
};

/* ──────────────────────────────────────────────
   💻 LAPTOP — screen size / resolution / display / storage / RAM / CPU
   (kept fully separate from Phone/TV so a 15.6" laptop size can never
   show up as a phone size, and vice versa)
────────────────────────────────────────────── */
export const laptopScreenSizeAttributes: AttributeItem = {
  id: 30030,
  name: "Screen Size",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.LAPTOP],
  values: [
    { id: 3003001, name: '11.6"', numericValue: 11.6, unit: "inch" },
    { id: 3003002, name: '13.3"', numericValue: 13.3, unit: "inch" },
    { id: 3003003, name: '14"', numericValue: 14, unit: "inch" },
    { id: 3003004, name: '15.6"', numericValue: 15.6, unit: "inch" },
    { id: 3003005, name: '16"', numericValue: 16, unit: "inch" },
    { id: 3003006, name: '17.3"', numericValue: 17.3, unit: "inch" },
  ],
};

export const laptopResolutionAttributes: AttributeItem = {
  id: 30031,
  name: "Resolution",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.LAPTOP],
  values: [
    { id: 3003101, name: "HD" },
    { id: 3003102, name: "Full HD" },
    { id: 3003103, name: "2K / QHD" },
    { id: 3003104, name: "4K UHD" },
    { id: 3003105, name: "Retina" },
  ],
};

export const laptopDisplayTypeAttributes: AttributeItem = {
  id: 30032,
  name: "Display Type",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.LAPTOP],
  values: [
    { id: 3003201, name: "IPS" },
    { id: 3003202, name: "OLED" },
    { id: 3003203, name: "Mini LED" },
    { id: 3003204, name: "Retina" },
    { id: 3003205, name: "Touchscreen" },
  ],
};

export const laptopStorageAttributes: AttributeItem = {
  id: 30033,
  name: "Storage",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.LAPTOP],
  values: [
    { id: 3003301, name: "128 GB SSD" },
    { id: 3003302, name: "256 GB SSD" },
    { id: 3003303, name: "512 GB SSD" },
    { id: 3003304, name: "1 TB SSD" },
    { id: 3003305, name: "2 TB SSD" },
    { id: 3003306, name: "1 TB HDD" },
  ],
};

export const laptopRamAttributes: AttributeItem = {
  id: 30034,
  name: "RAM",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.LAPTOP],
  values: [
    { id: 3003401, name: "4 GB" },
    { id: 3003402, name: "8 GB" },
    { id: 3003403, name: "16 GB" },
    { id: 3003404, name: "32 GB" },
    { id: 3003405, name: "64 GB" },
  ],
};

export const laptopProcessorAttributes: AttributeItem = {
  id: 30035,
  name: "Processor",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.LAPTOP],
  values: [
    { id: 3003501, name: "Intel Core i3" },
    { id: 3003502, name: "Intel Core i5" },
    { id: 3003503, name: "Intel Core i7" },
    { id: 3003504, name: "Intel Core i9" },
    { id: 3003505, name: "Apple M1" },
    { id: 3003506, name: "Apple M2" },
    { id: 3003507, name: "Apple M3" },
    { id: 3003508, name: "AMD Ryzen 5" },
    { id: 3003509, name: "AMD Ryzen 7" },
    { id: 3003510, name: "AMD Ryzen 9" },
  ],
};

/* ──────────────────────────────────────────────
   🏷️ ELECTRONICS BRANDS (dedicated, cleaned up —
   was previously buried inside a generic "common" bucket)
────────────────────────────────────────────── */
export const electronicsBrandAttributes: AttributeItem = {
  id: 30040,
  name: "Brand",
  values: [
    { id: 3004001, name: "Samsung" },
    { id: 3004002, name: "LG" },
    { id: 3004003, name: "Sony" },
    { id: 3004004, name: "Apple" },
    { id: 3004005, name: "HP" },
    { id: 3004006, name: "Dell" },
    { id: 3004007, name: "Xiaomi" },
    { id: 3004008, name: "OnePlus" },
    { id: 3004009, name: "Google" },
    { id: 3004010, name: "Nokia" },
    { id: 3004011, name: "Motorola" },
    { id: 3004012, name: "Asus" },
    { id: 3004013, name: "Lenovo" },
    { id: 3004014, name: "Microsoft" },
    { id: 3004015, name: "Razer" },
    { id: 3004016, name: "Toshiba" },
    { id: 3004017, name: "Panasonic" },
  ],
};

export const electronicsWarrantyAttributes: AttributeItem = {
  id: 30041,
  name: "Warranty",
  values: [
    { id: 3004101, name: "No Warranty" },
    { id: 3004102, name: "6 Months" },
    { id: 3004103, name: "1 Year" },
    { id: 3004104, name: "2 Years" },
    { id: 3004105, name: "3 Years" },
  ],
};

export const electronicsConditionAttributes: AttributeItem = {
  id: 30042,
  name: "Condition",
  values: [
    { id: 3004201, name: "New" },
    { id: 3004202, name: "Refurbished" },
    { id: 3004203, name: "Used" },
  ],
};

/* ──────────────────────────────────────────────
   Grouped by device — handy if a component just
   wants "every attribute group that applies to TVs"
────────────────────────────────────────────── */
export const tvAttributes: AttributeItem[] = [
  tvScreenSizeAttributes,
  tvResolutionAttributes,
  tvDisplayTypeAttributes,
  tvSmartAttributes,
];

export const phoneAttributes: AttributeItem[] = [
  phoneScreenSizeAttributes,
  phoneResolutionAttributes,
  phoneDisplayTypeAttributes,
  phoneStorageAttributes,
  phoneRamAttributes,
];

export const laptopAttributes: AttributeItem[] = [
  laptopScreenSizeAttributes,
  laptopResolutionAttributes,
  laptopDisplayTypeAttributes,
  laptopStorageAttributes,
  laptopRamAttributes,
  laptopProcessorAttributes,
];

/* ──────────────────────────────────────────────
   📱 TABLET — screen size / resolution / display / storage / RAM
────────────────────────────────────────────── */
export const tabletScreenSizeAttributes: AttributeItem = {
  id: 30050,
  name: "Screen Size",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.TABLET],
  values: [
    { id: 3005001, name: '7.9"',  numericValue: 7.9,  unit: "inch" },
    { id: 3005002, name: '8.3"',  numericValue: 8.3,  unit: "inch" },
    { id: 3005003, name: '9.7"',  numericValue: 9.7,  unit: "inch" },
    { id: 3005004, name: '10.2"', numericValue: 10.2, unit: "inch" },
    { id: 3005005, name: '10.9"', numericValue: 10.9, unit: "inch" },
    { id: 3005006, name: '11"',   numericValue: 11,   unit: "inch" },
    { id: 3005007, name: '12.9"', numericValue: 12.9, unit: "inch" },
    { id: 3005008, name: '14.6"', numericValue: 14.6, unit: "inch" },
  ],
};

export const tabletResolutionAttributes: AttributeItem = {
  id: 30051,
  name: "Resolution",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.TABLET],
  values: [
    { id: 3005101, name: "HD" },
    { id: 3005102, name: "Full HD" },
    { id: 3005103, name: "2K / QHD" },
    { id: 3005104, name: "Retina" },
    { id: 3005105, name: "Liquid Retina XDR" },
  ],
};

export const tabletDisplayTypeAttributes: AttributeItem = {
  id: 30052,
  name: "Display Type",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.TABLET],
  values: [
    { id: 3005201, name: "IPS LCD" },
    { id: 3005202, name: "OLED" },
    { id: 3005203, name: "Mini LED" },
    { id: 3005204, name: "Retina" },
  ],
};

export const tabletStorageAttributes: AttributeItem = {
  id: 30053,
  name: "Storage",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.TABLET],
  values: [
    { id: 3005301, name: "32 GB" },
    { id: 3005302, name: "64 GB" },
    { id: 3005303, name: "128 GB" },
    { id: 3005304, name: "256 GB" },
    { id: 3005305, name: "512 GB" },
    { id: 3005306, name: "1 TB" },
  ],
};

export const tabletRamAttributes: AttributeItem = {
  id: 30054,
  name: "RAM",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.TABLET],
  values: [
    { id: 3005401, name: "3 GB" },
    { id: 3005402, name: "4 GB" },
    { id: 3005403, name: "6 GB" },
    { id: 3005404, name: "8 GB" },
    { id: 3005405, name: "12 GB" },
    { id: 3005406, name: "16 GB" },
  ],
};

export const tabletConnectivityAttributes: AttributeItem = {
  id: 30055,
  name: "Connectivity",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.TABLET],
  values: [
    { id: 3005501, name: "Wi-Fi Only" },
    { id: 3005502, name: "Wi-Fi + Cellular" },
  ],
};

/* ──────────────────────────────────────────────
   ⌚ SMARTWATCH — case size / display / connectivity / band
────────────────────────────────────────────── */
export const smartwatchCaseSizeAttributes: AttributeItem = {
  id: 30060,
  name: "Case Size",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.SMARTWATCH],
  values: [
    { id: 3006001, name: "38 mm" },
    { id: 3006002, name: "40 mm" },
    { id: 3006003, name: "41 mm" },
    { id: 3006004, name: "42 mm" },
    { id: 3006005, name: "44 mm" },
    { id: 3006006, name: "45 mm" },
    { id: 3006007, name: "46 mm" },
    { id: 3006008, name: "49 mm" },
  ],
};

export const smartwatchDisplayTypeAttributes: AttributeItem = {
  id: 30061,
  name: "Display Type",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.SMARTWATCH],
  values: [
    { id: 3006101, name: "AMOLED" },
    { id: 3006102, name: "LCD" },
    { id: 3006103, name: "Retina LTPO OLED" },
    { id: 3006104, name: "E-Ink" },
  ],
};

export const smartwatchConnectivityAttributes: AttributeItem = {
  id: 30062,
  name: "Connectivity",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.SMARTWATCH],
  values: [
    { id: 3006201, name: "Bluetooth" },
    { id: 3006202, name: "Wi-Fi" },
    { id: 3006203, name: "GPS" },
    { id: 3006204, name: "LTE / Cellular" },
  ],
};

export const smartwatchBandMaterialAttributes: AttributeItem = {
  id: 30063,
  name: "Band Material",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.SMARTWATCH],
  values: [
    { id: 3006301, name: "Silicone" },
    { id: 3006302, name: "Leather" },
    { id: 3006303, name: "Stainless Steel" },
    { id: 3006304, name: "Nylon / Fabric" },
    { id: 3006305, name: "Titanium" },
  ],
};

export const smartwatchBatteryLifeAttributes: AttributeItem = {
  id: 30064,
  name: "Battery Life",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.SMARTWATCH],
  values: [
    { id: 3006401, name: "Up to 18 hours" },
    { id: 3006402, name: "1–2 days" },
    { id: 3006403, name: "3–7 days" },
    { id: 3006404, name: "Up to 2 weeks" },
    { id: 3006405, name: "Up to 1 month" },
  ],
};

/* ──────────────────────────────────────────────
   📷 CAMERA — sensor / megapixels / lens mount / video
────────────────────────────────────────────── */
export const cameraTypeAttributes: AttributeItem = {
  id: 30070,
  name: "Camera Type",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.CAMERA],
  values: [
    { id: 3007001, name: "DSLR" },
    { id: 3007002, name: "Mirrorless" },
    { id: 3007003, name: "Compact / Point & Shoot" },
    { id: 3007004, name: "Action Camera" },
    { id: 3007005, name: "Instant Camera" },
    { id: 3007006, name: "Film Camera" },
  ],
};

export const cameraMegapixelsAttributes: AttributeItem = {
  id: 30071,
  name: "Megapixels",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.CAMERA],
  values: [
    { id: 3007101, name: "Up to 12 MP" },
    { id: 3007102, name: "12–20 MP" },
    { id: 3007103, name: "20–24 MP" },
    { id: 3007104, name: "24–30 MP" },
    { id: 3007105, name: "30–45 MP" },
    { id: 3007106, name: "45+ MP" },
  ],
};

export const cameraSensorTypeAttributes: AttributeItem = {
  id: 30072,
  name: "Sensor Type",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.CAMERA],
  values: [
    { id: 3007201, name: "Full Frame" },
    { id: 3007202, name: "APS-C" },
    { id: 3007203, name: "Micro Four Thirds" },
    { id: 3007204, name: "1-inch" },
    { id: 3007205, name: "Medium Format" },
  ],
};

export const cameraLensMountAttributes: AttributeItem = {
  id: 30073,
  name: "Lens Mount",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.CAMERA],
  values: [
    { id: 3007301, name: "Canon RF" },
    { id: 3007302, name: "Canon EF" },
    { id: 3007303, name: "Nikon Z" },
    { id: 3007304, name: "Nikon F" },
    { id: 3007305, name: "Sony E" },
    { id: 3007306, name: "Fujifilm X" },
    { id: 3007307, name: "Micro Four Thirds" },
    { id: 3007308, name: "Fixed Lens" },
  ],
};

export const cameraVideoResolutionAttributes: AttributeItem = {
  id: 30074,
  name: "Max Video Resolution",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.CAMERA],
  values: [
    { id: 3007401, name: "1080p Full HD" },
    { id: 3007402, name: "4K" },
    { id: 3007403, name: "6K" },
    { id: 3007404, name: "8K" },
  ],
};

/* ──────────────────────────────────────────────
   🎧 HEADPHONES — form factor / connectivity / ANC
────────────────────────────────────────────── */
export const headphonesFormFactorAttributes: AttributeItem = {
  id: 30080,
  name: "Form Factor",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.HEADPHONES],
  values: [
    { id: 3008001, name: "Over-Ear" },
    { id: 3008002, name: "On-Ear" },
    { id: 3008003, name: "In-Ear / Earbuds" },
    { id: 3008004, name: "True Wireless" },
    { id: 3008005, name: "Neckband" },
  ],
};

export const headphonesConnectivityAttributes: AttributeItem = {
  id: 30081,
  name: "Connectivity",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.HEADPHONES],
  values: [
    { id: 3008101, name: "Wired" },
    { id: 3008102, name: "Bluetooth" },
    { id: 3008103, name: "Wireless (2.4GHz)" },
    { id: 3008104, name: "Bluetooth + Wired" },
  ],
};

export const headphonesNoiseControlAttributes: AttributeItem = {
  id: 30082,
  name: "Noise Control",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.HEADPHONES],
  values: [
    { id: 3008201, name: "Active Noise Cancelling (ANC)" },
    { id: 3008202, name: "Passive Isolation" },
    { id: 3008203, name: "Transparency Mode" },
    { id: 3008204, name: "None" },
  ],
};

export const headphonesBatteryLifeAttributes: AttributeItem = {
  id: 30083,
  name: "Battery Life",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.HEADPHONES],
  values: [
    { id: 3008301, name: "Up to 6 hours" },
    { id: 3008302, name: "6–12 hours" },
    { id: 3008303, name: "12–24 hours" },
    { id: 3008304, name: "24–40 hours" },
    { id: 3008305, name: "40+ hours" },
  ],
};

/* ──────────────────────────────────────────────
   🎮 GAMING CONSOLE — generation / storage / edition
────────────────────────────────────────────── */
export const consoleGenerationAttributes: AttributeItem = {
  id: 30090,
  name: "Generation",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.GAMING_CONSOLE],
  values: [
    { id: 3009001, name: "Current-Gen" },
    { id: 3009002, name: "Previous-Gen" },
    { id: 3009003, name: "Handheld" },
    { id: 3009004, name: "Retro / Classic" },
  ],
};

export const consoleStorageAttributes: AttributeItem = {
  id: 30091,
  name: "Storage",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.GAMING_CONSOLE],
  values: [
    { id: 3009101, name: "256 GB" },
    { id: 3009102, name: "512 GB" },
    { id: 3009103, name: "1 TB" },
    { id: 3009104, name: "2 TB" },
  ],
};

export const consoleEditionAttributes: AttributeItem = {
  id: 30092,
  name: "Edition",
  appliesTo: [ELECTRONICS_CATEGORY_IDS.GAMING_CONSOLE],
  values: [
    { id: 3009201, name: "Standard" },
    { id: 3009202, name: "Digital Edition" },
    { id: 3009203, name: "Limited / Collector's Edition" },
    { id: 3009204, name: "Bundle" },
  ],
};

/* ──────────────────────────────────────────────
   Grouped by device (added to the existing block)
────────────────────────────────────────────── */
export const tabletAttributes: AttributeItem[] = [
  tabletScreenSizeAttributes,
  tabletResolutionAttributes,
  tabletDisplayTypeAttributes,
  tabletStorageAttributes,
  tabletRamAttributes,
  tabletConnectivityAttributes,
];

export const smartwatchAttributes: AttributeItem[] = [
  smartwatchCaseSizeAttributes,
  smartwatchDisplayTypeAttributes,
  smartwatchConnectivityAttributes,
  smartwatchBandMaterialAttributes,
  smartwatchBatteryLifeAttributes,
];

export const cameraAttributes: AttributeItem[] = [
  cameraTypeAttributes,
  cameraMegapixelsAttributes,
  cameraSensorTypeAttributes,
  cameraLensMountAttributes,
  cameraVideoResolutionAttributes,
];

export const headphonesAttributes: AttributeItem[] = [
  headphonesFormFactorAttributes,
  headphonesConnectivityAttributes,
  headphonesNoiseControlAttributes,
  headphonesBatteryLifeAttributes,
];

export const gamingConsoleAttributes: AttributeItem[] = [
  consoleGenerationAttributes,
  consoleStorageAttributes,
  consoleEditionAttributes,
];

/** Applies to every electronics subcategory (no `appliesTo` restriction) */
export const commonElectronicsAttributes: AttributeItem[] = [
  electronicsBrandAttributes,
  electronicsWarrantyAttributes,
  electronicsConditionAttributes,
];

/* ======================================================
   📦 FINAL EXPORT — every electronics attribute group.
   Filter by `appliesTo?.includes(subcategoryId)` (or no
   `appliesTo` at all = applies everywhere) when rendering
   facets for a specific product subcategory.
   ====================================================== */
export const electronicsAttributes: AttributeItem[] = [
  productColorAttributes,
  ...tvAttributes,
  ...phoneAttributes,
  ...laptopAttributes,
  ...tabletAttributes,
  ...smartwatchAttributes,
  ...cameraAttributes,
  ...headphonesAttributes,
  ...gamingConsoleAttributes,
  ...commonElectronicsAttributes,
];